// ═══════════════════════════════════════
// Mensajería — extensiones Fase 4
// ═══════════════════════════════════════
// Agrega capacidades encima de lib/chat.js sin modificarlo:
//
//   - convertirMensajeEnTarea(): toma un mensaje completo y genera un accionable
//     asignado a un responsable, guardando el origen (mensajeId + conversacionId).
//   - convertirSeleccionEnTarea(): igual pero con un extracto del mensaje (texto
//     libre seleccionado por el usuario). Permite múltiples tareas por mensaje.
//
// Cada accionable generado queda con:
//   {
//     origen: {
//       tipo: 'mensaje',
//       conversacionId, mensajeId, empresaId,
//       extracto?: string    // si vino de selección
//     },
//     estado: 'propuesto',    // inicio del flujo: Dato → Mensaje → Aceptación → Tarea
//     ...
//   }
// ═══════════════════════════════════════

import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from './firebase'
import { COLLECTIONS } from './constants'

// Estados del flujo de accionables — definidos en tareas.js (sistema unificado)
import { ACCIONABLE_ESTADOS, crearAccionable } from './tareas'
export { ACCIONABLE_ESTADOS }

/**
 * Crea un accionable a partir de un mensaje COMPLETO.
 *
 * @param {Object} p
 * @param {string} p.conversacionId
 * @param {string} p.mensajeId
 * @param {string} p.emisorUserId        quien crea la tarea (típicamente el autor del mensaje)
 * @param {string} p.responsableUserId   quien debe hacerla
 * @param {string} [p.titulo]            si no se pasa, se usa el texto del mensaje truncado
 * @param {string} [p.descripcion]
 * @param {string} [p.empresaId]         si no se pasa, se lee de la conversación
 * @param {Date}   [p.vencimiento]
 * @param {string} [p.prioridad]         'baja'|'normal'|'alta'
 * @param {string} [p.slaId]             FK opcional al SLA aplicado
 */
export async function convertirMensajeEnTarea(p) {
  if (!p.conversacionId || !p.mensajeId) throw new Error('conversacionId y mensajeId requeridos')
  if (!p.emisorUserId || !p.responsableUserId) throw new Error('emisorUserId y responsableUserId requeridos')

  // Resolver empresaId y texto del mensaje si no vinieron
  let empresaId = p.empresaId || null
  let textoMensaje = ''
  let autorMensajeId = null

  const convSnap = await getDoc(doc(db, COLLECTIONS.CONVERSACIONES, p.conversacionId))
  if (convSnap.exists()) empresaId = empresaId || convSnap.data().empresaId || null

  const msgSnap = await getDoc(doc(db, COLLECTIONS.CONVERSACIONES, p.conversacionId, COLLECTIONS.MENSAJES, p.mensajeId))
  if (msgSnap.exists()) {
    const d = msgSnap.data()
    textoMensaje = d.texto || ''
    autorMensajeId = d.autorId || null
  }

  const titulo = (p.titulo && p.titulo.trim())
    || (textoMensaje ? textoMensaje.slice(0, 80) : 'Tarea')
  const descripcion = p.descripcion || (textoMensaje.length > 80 ? textoMensaje : '')

  return await crearAccionableBase({
    titulo,
    descripcion,
    empresaId,
    emisorUserId: p.emisorUserId,
    responsableUserId: p.responsableUserId,
    vencimiento: p.vencimiento || null,
    prioridad: p.prioridad || 'normal',
    slaId: p.slaId || null,
    origen: {
      tipo: 'mensaje',
      conversacionId: p.conversacionId,
      mensajeId: p.mensajeId,
      empresaId,
      autorMensajeId,
    },
  })
}

/**
 * Crea un accionable a partir de un EXTRACTO (selección de texto) de un mensaje.
 * Permite múltiples tareas por mensaje.
 */
export async function convertirSeleccionEnTarea(p) {
  if (!p.extracto || !p.extracto.trim()) throw new Error('extracto requerido')
  if (!p.conversacionId || !p.mensajeId) throw new Error('conversacionId y mensajeId requeridos')
  if (!p.emisorUserId || !p.responsableUserId) throw new Error('emisorUserId y responsableUserId requeridos')

  // Resolver empresaId si no vino
  let empresaId = p.empresaId || null
  if (!empresaId) {
    const convSnap = await getDoc(doc(db, COLLECTIONS.CONVERSACIONES, p.conversacionId))
    if (convSnap.exists()) empresaId = convSnap.data().empresaId || null
  }

  const extracto = p.extracto.trim()
  const titulo = (p.titulo && p.titulo.trim()) || extracto.slice(0, 80)

  return await crearAccionableBase({
    titulo,
    descripcion: p.descripcion || extracto,
    empresaId,
    emisorUserId: p.emisorUserId,
    responsableUserId: p.responsableUserId,
    vencimiento: p.vencimiento || null,
    prioridad: p.prioridad || 'normal',
    slaId: p.slaId || null,
    origen: {
      tipo: 'mensaje',
      conversacionId: p.conversacionId,
      mensajeId: p.mensajeId,
      empresaId,
      extracto,    // preservamos el texto exacto elegido
    },
  })
}

// ─── helper interno: creador base de accionable ────────────
// Delega a crearAccionable() del sistema unificado (subcollección empresas/{empId}/tareas)
// y luego marca el mensaje de origen si corresponde.
async function crearAccionableBase({
  titulo, descripcion, empresaId, emisorUserId, responsableUserId,
  vencimiento, prioridad, slaId, origen,
}) {
  if (!empresaId) {
    console.warn('[crearAccionableBase] empresaId requerido para sistema unificado')
    return null
  }

  // Crear via sistema unificado
  const accId = await crearAccionable(empresaId, {
    titulo,
    descripcion: descripcion || '',
    emisorUserId,
    responsableUserId,
    vencimiento: vencimiento || null,
    prioridad: prioridad || 'normal',
    slaId: slaId || null,
    origen: origen || null,
    estado: ACCIONABLE_ESTADOS.PROPUESTO,
  })

  // Si vino desde un mensaje, dejamos una marca en el mensaje (para UI "este mensaje tiene N tareas")
  if (origen?.tipo === 'mensaje' && origen.conversacionId && origen.mensajeId) {
    try {
      const msgRef = doc(db, COLLECTIONS.CONVERSACIONES, origen.conversacionId, COLLECTIONS.MENSAJES, origen.mensajeId)
      const snap = await getDoc(msgRef)
      if (snap.exists()) {
        const data = snap.data()
        const tareasIds = Array.isArray(data.tareasIds) ? data.tareasIds : []
        await updateDoc(msgRef, {
          tareasIds: Array.from(new Set([...tareasIds, accId])),
        })
      }
    } catch (e) {
      console.warn('[convertirMensajeEnTarea] marca en mensaje falló:', e)
    }
  }

  return { id: accId, empresaId, titulo }
}

// Exposición en window
if (typeof window !== 'undefined') {
  window.__convertirMensajeEnTarea = convertirMensajeEnTarea
  window.__convertirSeleccionEnTarea = convertirSeleccionEnTarea
}
